/* eslint-disable @typescript-eslint/no-explicit-any */
import { DataConfig } from "@/components/table/useDefaultDataConfig";
import { REQUIRED_MESSAGE, YEAR_VALIDATION_MESSAGE } from "@/utils/constant";
import { generateParams } from "@/utils/helper/generateParams";
import { axiosInstance } from "@/utils/lib/axios";
import {
  QueryFunctionContext,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { z } from "zod";
import { Paginated } from "./types";
import { stringRange } from "@/utils/helper/refinement";

const apdFilePayloadSchema = z.object({
  file: z
    .instanceof(File, { message: REQUIRED_MESSAGE })
    .refine(
      (file) =>
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel",
      {
        message: "Please upload files in .xlxs format only",
      },
    ),
});
type APDFilePayloadSchema = z.infer<typeof apdFilePayloadSchema>;

const apdPayloadSchema = z
  .object({
    cityTown: z
      .string()
      .min(1, { message: REQUIRED_MESSAGE })
      .refine(...stringRange(4, 50)),
    hpPermit: z
      .string()
      .min(1, { message: REQUIRED_MESSAGE })
      .refine(...stringRange(1, 10)),
    hpDesign: z
      .string()
      .min(1, { message: REQUIRED_MESSAGE })
      .refine(...stringRange(1, 10)),
    suffixPermit: z.string().min(1, { message: REQUIRED_MESSAGE }),
    clusterId: z
      .string()
      .min(1, { message: REQUIRED_MESSAGE })
      .refine(...stringRange(9, 50)),
    projectName: z
      .string()
      .min(1, { message: REQUIRED_MESSAGE })
      .refine(...stringRange(10, 250)),
    fdtId: z
      .string()
      .min(1, { message: REQUIRED_MESSAGE })
      .refine(...stringRange(8, 9)),
    drawingIdApd: z
      .string()
      .min(1, { message: REQUIRED_MESSAGE })
      .refine(...stringRange(14, 50)),
    oltType: z.string().min(1, { message: REQUIRED_MESSAGE }),
    oltIdaction: z.string().min(1, { message: REQUIRED_MESSAGE }),
    combineStatus: z
      .string()
      .min(1, { message: REQUIRED_MESSAGE })
      .refine(...stringRange(11, 14)),
    latitude: z
      .string()
      .min(1, { message: REQUIRED_MESSAGE })
      .refine(...stringRange(8, 10)),
    longitude: z
      .string()
      .min(1, { message: REQUIRED_MESSAGE })
      .refine(...stringRange(8, 10)),
    rollOutType: z
      .string()
      .min(1, { message: REQUIRED_MESSAGE })
      .refine(...stringRange(10, 250)),
    tenantName: z.string().min(1, { message: REQUIRED_MESSAGE }),
    profileName: z.string().min(1, { message: REQUIRED_MESSAGE }),
    yearTarget: z
      .string()
      .min(4, { message: YEAR_VALIDATION_MESSAGE })
      .max(4, { message: YEAR_VALIDATION_MESSAGE })
      .refine((value) => !value || /^\d+$/.test(value), {
        message:
          "The year must be a number, without any letters or other characters.",
      }),
    monthTarget: z.coerce.number().min(1, { message: REQUIRED_MESSAGE }),
    vendor: z.string().min(1, { message: REQUIRED_MESSAGE }),
    ITV_MIN_SUFFIX_PERMIT_DIGIT: z.coerce.number().optional(),
    ITV_MAX_SUFFIX_PERMIT_DIGIT: z.coerce.number().optional(),
  })
  .superRefine((data, ctx) => {
    const {
      suffixPermit,
      ITV_MAX_SUFFIX_PERMIT_DIGIT = 9,
      ITV_MIN_SUFFIX_PERMIT_DIGIT = 50,
    } = data;
    const length = suffixPermit.length;

    if (
      !(
        length >= ITV_MIN_SUFFIX_PERMIT_DIGIT &&
        length <= ITV_MAX_SUFFIX_PERMIT_DIGIT
      )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Must input between ${ITV_MIN_SUFFIX_PERMIT_DIGIT} - ${ITV_MAX_SUFFIX_PERMIT_DIGIT} characters`,
        path: ["suffixPermit"],
      });
    }
  });
type APDPayloadSchema = z.infer<typeof apdPayloadSchema>;

type APDData = {
  id: string;
  clusterIdAbd: string;
  cityTown: string;
  hpPermit: string;
  hpDesign: string;
  suffixPermit: string;
  clusterId: string;
  projectName: string;
  fdtId: string;
  drawingIdAbd: string;
  drawingIdApd: string;
  oltType: string;
  oltTypeString: string;
  oltIdaction: string;
  combineStatus: string;
  latitude: string;
  longitude: string;
  rollOutType: string;
  profileName: string;
  yearTarget: number;
  monthTarget: number;
  tenantName: string;
  vendor: string;
};

/* ======== *
 * READ APD *
 * ======== */

type APDsQueryKey = DataConfig & {
  clusterId?: string;
  permitId?: string;
  search?: string;
  vendor?: string;
};
interface APDList extends Paginated {
  content: APDData[];
}

async function getAPDs(
  queryFunctionContext: QueryFunctionContext,
): Promise<APDList> {
  const { queryKey } = queryFunctionContext;
  const [_, apdsQueryKey] = queryKey;
  const params = generateParams(apdsQueryKey as APDsQueryKey);

  const response = await axiosInstance.get(
    "/api/crud/trackerdata-apd" + params,
  );
  return response.data.data;
}

function useAPDs(
  apdsQueryKey: APDsQueryKey = {},
  options?: {
    enabled?: boolean;
  },
) {
  return useQuery({
    ...options,
    queryKey: ["apd", apdsQueryKey],
    queryFn: getAPDs,
  });
}

interface APDQueryKey {
  id: string;
}

async function getAPD(
  queryFunctionContext: QueryFunctionContext,
): Promise<APDData> {
  const { queryKey } = queryFunctionContext;
  const [_, id] = queryKey;

  const response = await axiosInstance.get(`/api/crud/trackerdata-apd/${id}`);
  return response.data.data;
}

function useAPD(apdQueryKey: APDQueryKey) {
  const { id } = apdQueryKey;
  return useQuery({
    queryKey: ["apd", id],
    queryFn: getAPD,
  });
}

/* ========== *
 * CREATE APD *
 * ========== */

async function createAPD(data: APDFilePayloadSchema) {
  const response = await axiosInstance.post(
    "/api/crud/trackerdata-apd/excel",
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data.data;
}

function useAPDCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: APDFilePayloadSchema }) => createAPD(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["apd"] });
    },
  });
}

async function validationAPD(
  data: {
    warnMessages: any[];
    warnMessageAllRows: any[];
  },
  fileName: string,
) {
  const response = await axiosInstance.post(
    `/api/crud/trackerdata-apd/excel/validation`,
    data,
    {
      responseType: "blob",
    },
  );

  downloadFile(new Blob([response.data]), fileName);
}

function useAPDValidation() {
  return useMutation({
    mutationFn: ({
      data,
      fileName,
    }: {
      data: {
        warnMessages: any[];
        warnMessageAllRows: any[];
      };
      fileName: string;
    }) => validationAPD(data, fileName),
  });
}

/* ========== *
 * EDIT BULK APD *
 * ========== */

async function editBulkAPD(data: APDFilePayloadSchema): Promise<APDData[]> {
  const response = await axiosInstance.post(
    "/api/crud/trackerdata-apd/update/excel",
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );

  return response.data.data;
}

function useAPDEditBulk() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: APDFilePayloadSchema }) => editBulkAPD(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["apd"] });
    },
  });
}

/* ========== *
 * EDIT BULK COMBINE STATUS APD *
 * ========== */

async function editBulkCombineStatusAPD(
  data: APDFilePayloadSchema,
): Promise<APDData[]> {
  const response = await axiosInstance.post(
    "/api/crud/trackerdata-apd/update/excel/combine-status",
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );

  return response.data.data;
}

function useAPDCombineStatusEditBulk() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: APDFilePayloadSchema }) =>
      editBulkCombineStatusAPD(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["apd"] });
    },
  });
}

/* ========== *
 * EDIT APD *
 * ========== */

async function editAPD(id: string, data: APDPayloadSchema): Promise<APDData> {
  const response = await axiosInstance.post(
    `/api/crud/trackerdata-apd/${id}`,
    data,
  );
  return response.data.data;
}

function useAPDEdit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: APDPayloadSchema }) =>
      editAPD(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["apd"] });
    },
  });
}

/* ========== *
 * EDIT Combine Status APD *
 * ========== */

async function editCombineStatusAPD(id: string, data: APDPayloadSchema) {
  const payload = {
    cityTown: data.cityTown,
    hpPermit: data.hpPermit,
    hpDesign: data.hpDesign,
    suffixPermit: data.suffixPermit,
    clusterId: data.clusterId,
    projectName: data.projectName,
    fdtId: data.fdtId,
    drawingIdApd: data.drawingIdApd,
    oltType: data.oltType,
    oltIdaction: data.oltIdaction,
    combineStatus: data.combineStatus,
    latitude: data.latitude,
    longitude: data.longitude,
    tenantName: data.tenantName,
    vendor: data.vendor,
  };

  const response = await axiosInstance.post(
    `/api/crud/trackerdata-apd/${id}/combine-status`,
    payload,
  );
  return response.data.data;
}

function useAPDCombineStatusEdit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: APDPayloadSchema }) =>
      editCombineStatusAPD(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["apd"] });
    },
  });
}

async function waitForMediaCompletion(
  url: string,
  interval = 2000,
): Promise<{ status: string; path?: string; originalFileName?: string }> {
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const {
      data: {
        data: { status, path, originalFileName },
      },
    } = await axiosInstance.get(`${url}`);

    if (status === "SUCCESS" || status === "ERROR") {
      return {
        status,
        path,
        originalFileName: originalFileName ?? "list-trackerdata-apd.xlsx",
      };
    }

    await new Promise((resolve) => setTimeout(resolve, interval));
  }
}

async function exportTrackerDataApd(
  config: DataConfig = {},
  isDownload: boolean = false,
  onDownload: boolean = false,
): Promise<{ status: string; fileName?: string }> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any

  let createResponse: any = {};
  if (!isDownload) {
    createResponse = await axiosInstance.get(
      `/api/crud/trackerdata-apd/excel${generateParams(config)}`,
    );
    localStorage.setItem("isDownloadApd", new Date().toISOString());
  }

  const downloadParams = {
    className: "com.alurkerja.crud.trackerdataapd.TrackerdataApd",
    entityId: config.entityId,
  };
  const { status, path, originalFileName } = await waitForMediaCompletion(
    !isDownload
      ? `/api/crud/media/${createResponse.data.data.id}`
      : `/api/crud/media/last${generateParams(downloadParams)}`,
  );

  if (onDownload && status === "SUCCESS") {
    const downloadResponse = await axiosInstance.get(
      "/api/crud/media/download",
      {
        params: { path, fileName: originalFileName, entityId: config.entityId },
        responseType: "blob",
      },
    );

    downloadFile(new Blob([downloadResponse.data]), originalFileName);
    localStorage.removeItem("isDownloadApd");
    return { status: "IDLE" };
  } else {
    return { status };
  }
}

function downloadFile(blobData: Blob, fileName: string | undefined): void {
  const url = window.URL.createObjectURL(blobData);
  const link = document.createElement("a");
  link.href = url;
  if (typeof fileName === "string") {
    link.setAttribute("download", fileName);
  }
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function useExportTrackerDataApd() {
  return useMutation({
    mutationFn: async ({
      config,
      isDownload,
      onDownload,
    }: {
      config?: DataConfig;
      isDownload?: boolean;
      onDownload?: boolean;
    }) => {
      return await exportTrackerDataApd(config, isDownload, onDownload);
    },
  });
}

export type { APDFilePayloadSchema, APDPayloadSchema, APDData };
export {
  apdFilePayloadSchema,
  apdPayloadSchema,
  useAPDs,
  useAPD,
  useAPDCreate,
  useAPDEdit,
  useAPDEditBulk,
  useAPDCombineStatusEdit,
  useAPDCombineStatusEditBulk,
  useExportTrackerDataApd,
  waitForMediaCompletion,
  downloadFile,
  exportTrackerDataApd,
  useAPDValidation,
};
